import React from 'react';
import { Clock3, Heart, MapPin } from 'lucide-react';
import { ClassData, getFullImageUrl } from '../services/api';
import { getNameInitials } from '../utils/userInitials';
import {
  getPeekPassDate,
  getPeekPassEventDate,
  getPeekPassOrganizer,
  getPeekPassSummary,
  getPeekPassTitle,
  getPeekPassVenue,
  getPeekPassVenueAddress,
  mapCategoryToEvent,
  mapDifficultyToAvailability,
  mapDifficultyToExperience
} from '../utils/peekPassBrand';

interface OrganizerCardProps {
  pack: ClassData;
  onViewDetails: (packId: string) => void;
}

const OrganizerCard: React.FC<OrganizerCardProps> = ({ pack, onViewDetails }) => {
  const displayTitle = getPeekPassTitle(pack);
  const displayCategory = mapCategoryToEvent(pack.category);
  const displaySeller = getPeekPassOrganizer(pack.organizer?.name);
  const displayStatus = mapDifficultyToAvailability(pack.difficulty);
  const displayMode = mapDifficultyToExperience(pack.difficulty);
  const displaySummary = getPeekPassSummary(pack);
  const displayDate = getPeekPassDate(pack);
  const displayVenue = getPeekPassVenue(pack);
  const displayVenueAddress = getPeekPassVenueAddress(pack);
  const eventDate = getPeekPassEventDate(pack);
  const effectiveDiscount = typeof pack.discount === 'number' ? pack.discount : 0;
  const [venueName, venueLocation] = displayVenue.split('·').map((value) => value.trim());
  const displayMonth = new Intl.DateTimeFormat('es-AR', { month: 'short' }).format(eventDate).replace('.', '').toUpperCase();
  const displayDay = new Intl.DateTimeFormat('es-AR', { day: '2-digit' }).format(eventDate);
  const displayYear = new Intl.DateTimeFormat('es-AR', { year: 'numeric' }).format(eventDate);
  const displayTime = new Intl.DateTimeFormat('es-AR', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  }).format(eventDate);
  const ticketDetails = pack.ticketCapacity
    ? `${pack.ticketType || displayMode} · ${pack.ticketCapacity} lugares`
    : displayMode;

  const handleVideoClick = () => onViewDetails(pack._id || pack.title);

  return (
    <article
      role="button"
      tabIndex={0}
      aria-label={`Ver detalle de ${displayTitle}`}
      onClick={handleVideoClick}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          handleVideoClick();
        }
      }}
      className="organizer-card group flex h-full cursor-pointer flex-col overflow-hidden rounded-[1.7rem] border border-slate-200 bg-white/95 shadow-[0_24px_52px_-34px_rgba(15,23,42,0.28)] transition duration-300 hover:-translate-y-1.5 hover:border-black/20 hover:shadow-[0_34px_70px_-40px_rgba(15,23,42,0.38)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#dff38c]"
    >
      <div className="relative h-60 overflow-hidden bg-slate-100 text-left">
        <img
          src={pack.thumbnail ? getFullImageUrl(pack.thumbnail) : 'https://placehold.co/1200x900/e5e7eb/111827?text=PEEK+PASS'}
          alt={displayTitle}
          className="h-full w-full object-cover transition duration-700 group-hover:scale-105"
          onError={(e) => {
            e.currentTarget.src = 'https://placehold.co/1200x900/e5e7eb/111827?text=PEEK+PASS';
          }}
        />
        <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(15,23,42,0.06)_0%,rgba(15,23,42,0.14)_38%,rgba(15,23,42,0.84)_100%)]" />

        <div className="absolute left-3 top-3 inline-flex items-center rounded-full border border-white/20 bg-white/92 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-800 shadow-sm">
          {displayCategory}
        </div>

        <div className={`absolute right-3 top-3 inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[10px] font-semibold shadow-sm backdrop-blur ${pack.likes?.length ? 'border-rose-200 bg-rose-50/95 text-rose-600' : 'border-white/20 bg-black/70 text-white'}`}>
          <Heart className={`h-3.5 w-3.5 ${pack.likes?.length ? 'fill-current' : ''}`} />
          {pack.likes?.length || 0}
        </div>

        <div className="absolute right-3 top-12 inline-flex items-center gap-1.5 rounded-full border border-white/20 bg-black/70 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-white shadow-sm backdrop-blur">
          <span className="h-2 w-2 rounded-full bg-emerald-400" />
          {displayMode}
        </div>

        {effectiveDiscount ? (
          <div className="absolute bottom-3 left-3 rounded-full bg-white/92 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-900 shadow-lg">
            Early access -{effectiveDiscount}%
          </div>
        ) : null}

        <div className="absolute inset-x-3 bottom-3">
          <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-white/70">Fecha</p>
          <p className="text-xs font-semibold text-white">{displayDate}</p>
        </div>
      </div>

      <div className="card-content flex flex-1 flex-col p-5">
        <div className="mb-3 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">
          <MapPin className="h-3.5 w-3.5 shrink-0 text-[#71931b]" />
          <p className="truncate">{displayVenueAddress || displayVenue}</p>
        </div>

        <div className="mb-4">
          <h3 className="text-[1.45rem] font-black tracking-[-0.055em] text-slate-900">{displayTitle}</h3>
          <p className="mt-2 line-clamp-2 text-sm leading-6 text-slate-600">{displaySummary}</p>
        </div>

        <div className="mb-5 flex items-center gap-3 rounded-[1.35rem] bg-slate-50/80 px-3.5 py-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-black text-xs font-bold text-white">
            {getNameInitials(displaySeller, undefined, 'VO')}
          </div>
          <div className="min-w-0">
            <p className="truncate text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500">Organiza</p>
            <p className="truncate text-sm font-semibold text-slate-900">{displaySeller}</p>
          </div>
        </div>

        <div className="mb-5 rounded-[1.45rem] border border-slate-200 bg-[linear-gradient(180deg,#ffffff_0%,#f8fafc_100%)] p-4">
          <div className="flex items-center gap-4">
            <div className="flex min-w-[88px] items-center gap-3 border-r border-slate-200 pr-4">
              <div className="text-center">
                <p className="text-[10px] font-black uppercase tracking-[0.18em] text-[#71931b]">{displayMonth}</p>
                <p className="mt-1 text-[2rem] font-black leading-none tracking-[-0.08em] text-slate-950">{displayDay}</p>
                <p className="mt-1 text-[11px] font-semibold text-slate-500">{displayYear}</p>
              </div>
            </div>

            <div className="min-w-0 flex-1">
              <div className="flex min-w-0 items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">
                <Clock3 className="h-3.5 w-3.5 shrink-0 text-[#71931b]" />
                <span className="shrink-0 whitespace-nowrap">{displayTime}HS</span>
                <span className="shrink-0 text-slate-300">/</span>
                <span className="min-w-0 truncate">{displayStatus}</span>
              </div>
              <p className="mt-2 text-sm font-semibold leading-5 text-slate-900">
                {venueLocation ? `${venueName} · ${venueLocation}` : venueName}
              </p>
              {displayVenueAddress ? <p className="mt-1 truncate text-xs text-slate-500">{displayVenueAddress}</p> : null}
              <p className="mt-1 text-xs uppercase tracking-[0.16em] text-slate-500">{ticketDetails}</p>
            </div>
          </div>
        </div>

      </div>
    </article>
  );
};

export default OrganizerCard;
